import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { DOCUMENT } from '@angular/common';
import { languages } from '../../../constants/language.constants';

const imgixHost = environment.imgixHost;

// Add to this list only if a new URL param in lime-main will greatly affect the content that is shown on the page
const whitelistParams = ['lang', 'city', 'property', 'guests'];

// Common articles, conjuctions, & prepositions for each language
const blacklistWords = ['am', 'del', 'sul', 'do', 'de', 'la', 'dei'];

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    public meta: Meta,
    private readonly titleService: Title,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.router.events.subscribe((navigation) => {
      if (navigation instanceof NavigationEnd) {
        this.updateRobotsMeta(navigation);

        const url = this.document.location.href;

        const sanitizedUrl = this.sanitizeUrl(url);
        this.updateAlternateUrls(sanitizedUrl);
        this.updateCanonicalUrl(sanitizedUrl);
      }
    });
  }

  handleCityCapitalization(cityName: string) {
    const split = cityName.split(' ');
    const capitalized = split.map((s, index) => {
      if (blacklistWords.includes(s.toLowerCase()) && index !== 0) {
        return s;
      } else {
        return s.charAt(0).toUpperCase() + s.slice(1);
      }
    });

    return capitalized.join(' ');
  }

  generateTags(config?: any) {
    config = Object.assign(this.getDefaultTags(), config);

    if (config.title) {
      this.titleService.setTitle(this.translateService.instant(config.title));
      this.meta.updateTag({ property: 'og:title', content: this.translateService.instant(config.title) });
      this.meta.updateTag({ name: 'twitter:title', content: this.translateService.instant(config.title) });
    }

    if (config.description) {
      this.meta.updateTag({
        property: 'og:description',
        content: this.translateService.instant(config.description)
      });
      this.meta.updateTag({
        property: 'description',
        content: this.translateService.instant(config.description)
      });
      this.meta.updateTag({
        name: 'twitter:description',
        content: this.translateService.instant(config.description)
      });
    }

    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
      this.meta.updateTag({ name: 'twitter:image', content: config.image });
    }

    if (config.slug) this.meta.updateTag({ property: 'og:url', content: `https://www.limehome.com/${config.slug}` });

    if (config.keywords)
      this.meta.updateTag({ property: 'keywords', content: this.translateService.instant(config.keywords) });

    this.meta.updateTag({ property: 'robots', content: 'index,follow' });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
    this.meta.updateTag({ property: 'og:site_name', content: 'limehome' });
  }

  getDefaultTags(): any {
    return {
      title: 'meta-title',
      description: 'meta-description',
      keywords: 'meta-keywords',
      image: `${imgixHost}/website/hero/hero-bg.jpeg?w=1200&h=630&fit=crop`,
      slug: ''
    };
  }

  // Source : https://developers.google.com/search/docs/guides/javascript-seo-basics?hl=en
  updateRobotsMeta(navigation) {
    // Regex to check if the current page is checkout or confirmation
    const confirmationUrlPattern = new RegExp('^(/listings/)(confirmation|checkout)');
    // tell Googlebot to exclude the checkout and the confirmation pages from the index
    const metaContent = confirmationUrlPattern.test(navigation.url) ? 'noindex, nofollow' : 'index, follow';

    if (this.meta.getTag('name="robots"')) {
      this.meta.updateTag({ name: 'robots', content: metaContent }, "name='robots'");
    } else {
      this.meta.addTags([{ name: 'robots', content: metaContent }]);
    }
  }

  sanitizeUrl(inputUrl: string): string {
    const url = new URL(inputUrl);
    const params = url.searchParams;

    const paramsToRemove = Array.from(params.keys()).filter((param) => !whitelistParams.includes(param));
    paramsToRemove.forEach((param) => {
      params.delete(param);
    });

    params.sort();

    const newUrl = `${url.origin}${url.pathname}${url.search}`;
    return newUrl;
  }

  updateCanonicalUrl(sanitizedUrl: string): void {
    const head = this.document.getElementsByTagName('head')[0];
    let element: HTMLLinkElement = this.document.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.document.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', sanitizedUrl);
  }

  updateAlternateUrls(sanitizedUrl: string): void {
    const head = this.document.getElementsByTagName('head')[0];

    // Map through all supported languages and either find matching tag or add one
    Object.values(languages).forEach((lang) => {
      let element: HTMLLinkElement = this.document.querySelector(`link[hreflang="${lang}"]`) || null;
      if (element == null) {
        element = this.document.createElement('link') as HTMLLinkElement;
        head.appendChild(element);
      }
      // Add / update lang param
      const sanitizedUrlObj = new URL(sanitizedUrl);
      sanitizedUrlObj.searchParams.set('lang', lang);
      sanitizedUrlObj.searchParams.sort();

      element.setAttribute('hreflang', lang);
      element.setAttribute('rel', 'alternate');
      element.setAttribute('href', sanitizedUrlObj.toString());
    });

    // X-Default (no lang param)
    let element: HTMLLinkElement = this.document.querySelector(`link[hreflang="x-default"]`) || null;
    if (element == null) {
      element = this.document.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    // Remove lang param
    const sanitizedUrlObj = new URL(sanitizedUrl);
    sanitizedUrlObj.searchParams.delete('lang');
    sanitizedUrlObj.searchParams.sort();

    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', 'x-default');
    element.setAttribute('href', sanitizedUrlObj.toString());
  }
}
